import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  buildQueryString,
  hasOwn,
  toPositiveInt,
} from "../client";

const ASSESSMENT_TAGS_BASE_PATH = "/admin/assessment-bank/tags";

function normalizeAssessmentTagPayload(data = {}) {
  const payload = {};
  const src = data && typeof data === "object" ? data : {};

  if (hasOwn(src, "title")) {
    payload.title = String(src.title ?? "").trim();
  }

  if (hasOwn(src, "slug")) {
    payload.slug = String(src.slug ?? "").trim();
  }

  if (hasOwn(src, "is_active")) {
    payload.is_active = Number(src.is_active) ? 1 : 0;
  }

  if (hasOwn(src, "parent_id")) {
    payload.parent_id = toPositiveInt(src.parent_id) || null;
  }

  return payload;
}

export async function fetchAssessmentTags({ activeOnly = false } = {}) {
  return apiGet(
    `${ASSESSMENT_TAGS_BASE_PATH}${buildQueryString({
      active_only: activeOnly ? 1 : "",
    })}`
  );
}

export async function createAssessmentTag(data) {
  return apiPost(ASSESSMENT_TAGS_BASE_PATH, normalizeAssessmentTagPayload(data));
}

export async function updateAssessmentTag(id, data) {
  const tagId = toPositiveInt(id);
  if (!tagId) throw new Error("assessment tag id invalid");

  return apiPut(
    `${ASSESSMENT_TAGS_BASE_PATH}/${tagId}`,
    normalizeAssessmentTagPayload(data)
  );
}

export async function deleteAssessmentTag(id) {
  const tagId = toPositiveInt(id);
  if (!tagId) throw new Error("assessment tag id invalid");
  return apiDelete(`${ASSESSMENT_TAGS_BASE_PATH}/${tagId}`);
}

function normalizeAssessmentBankQuestionType(rawType) {
  return String(rawType || "").trim().toLowerCase() === "essay" ? "essay" : "mcq";
}

function normalizeAssessmentBankOptions(rawOptions = []) {
  if (!Array.isArray(rawOptions)) return [];

  return rawOptions.map((option, index) => ({
    option_text: String(option?.option_text || option?.text || ""),
    is_correct: !!option?.is_correct,
    order_index:
      Number.isInteger(Number(option?.order_index)) &&
      Number(option?.order_index) > 0
        ? Number(option.order_index)
        : index + 1,
  }));
}

function normalizeAssessmentBankQuestionPayload(data = {}) {
  const payload = { ...(data || {}) };

  if (payload.bank_id != null) {
    payload.bank_id = toPositiveInt(payload.bank_id);
  }

  payload.question_type = normalizeAssessmentBankQuestionType(payload.question_type);
  payload.question_text = String(payload.question_text || "");
  payload.explanation = String(payload.explanation || "");
  payload.question_image = String(payload.question_image || "").trim();

  if (payload.order_index != null) {
    const orderIndex = Number(payload.order_index || 0);
    payload.order_index = Number.isInteger(orderIndex) && orderIndex > 0 ? orderIndex : 1;
  }

  if (Array.isArray(payload.tag_ids)) {
    payload.tag_ids = [
      ...new Set(
        payload.tag_ids
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value > 0)
      ),
    ];
  } else {
    payload.tag_ids = [];
  }

  if (payload.question_type === "mcq") {
    payload.options = normalizeAssessmentBankOptions(payload.options);
  } else {
    delete payload.options;
  }

  delete payload.tags;
  delete payload.tags_csv;
  delete payload.difficulty;
  delete payload.assessment_difficulty;

  return payload;
}

function normalizeAssessmentBankPayload(data = {}) {
  return {
    title: String(data?.title ?? "").trim(),
    description: data?.description == null ? "" : String(data.description),
    is_active: Number(data?.is_active) === 0 ? 0 : 1,
  };
}

export async function fetchAssessmentBanks() {
  return apiGet("/admin/assessment-bank/banks");
}

export async function fetchAssessmentBankChapters(bankId) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("assessment bank id invalid");
  return apiGet(`/admin/assessment-bank/banks/${id}/chapters`);
}

export async function createAssessmentBankChapter(bankId, data) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("assessment bank id invalid");
  return apiPost(`/admin/assessment-bank/banks/${id}/chapters`, {
    title: String(data?.title ?? "").trim(),
  });
}

export async function updateAssessmentBankChapter(chapterId, data) {
  const id = toPositiveInt(chapterId);
  if (!id) throw new Error("assessment bank chapter id invalid");
  return apiPut(`/admin/assessment-bank/chapters/${id}`, {
    title: String(data?.title ?? "").trim(),
    order_index: toPositiveInt(data?.order_index),
    is_active: Number(data?.is_active) === 0 ? 0 : 1,
  });
}

export async function deleteAssessmentBankChapter(chapterId) {
  const id = toPositiveInt(chapterId);
  if (!id) throw new Error("assessment bank chapter id invalid");
  return apiDelete(`/admin/assessment-bank/chapters/${id}`);
}

export async function fetchAssessmentBankSections(chapterId) {
  const id = toPositiveInt(chapterId);
  if (!id) throw new Error("assessment bank chapter id invalid");
  return apiGet(`/admin/assessment-bank/chapters/${id}/sections`);
}

export async function createAssessmentBankSection(chapterId, data) {
  const id = toPositiveInt(chapterId);
  if (!id) throw new Error("assessment bank chapter id invalid");
  return apiPost(`/admin/assessment-bank/chapters/${id}/sections`, {
    title: String(data?.title ?? "").trim(),
  });
}

export async function updateAssessmentBankSection(sectionId, data) {
  const id = toPositiveInt(sectionId);
  if (!id) throw new Error("assessment bank section id invalid");
  return apiPut(`/admin/assessment-bank/sections/${id}`, {
    title: String(data?.title ?? "").trim(),
    order_index: toPositiveInt(data?.order_index),
    is_active: Number(data?.is_active) === 0 ? 0 : 1,
  });
}

export async function deleteAssessmentBankSection(sectionId) {
  const id = toPositiveInt(sectionId);
  if (!id) throw new Error("assessment bank section id invalid");
  return apiDelete(`/admin/assessment-bank/sections/${id}`);
}

export async function fetchAssessmentBankHierarchyBranches(sectionId) {
  const id = toPositiveInt(sectionId);
  if (!id) throw new Error("assessment bank section id invalid");
  return apiGet(`/admin/assessment-bank/sections/${id}/branches`);
}

export async function createAssessmentBankBranch(sectionId, data) {
  const id = toPositiveInt(sectionId);
  if (!id) throw new Error("assessment bank section id invalid");
  return apiPost(`/admin/assessment-bank/sections/${id}/branches`, {
    title: String(data?.title ?? "").trim(),
  });
}

export async function updateAssessmentBankBranch(branchId, data) {
  const id = toPositiveInt(branchId);
  if (!id) throw new Error("assessment bank branch id invalid");
  return apiPut(`/admin/assessment-bank/branches/${id}`, {
    title: String(data?.title ?? "").trim(),
    order_index: toPositiveInt(data?.order_index),
    is_active: Number(data?.is_active) === 0 ? 0 : 1,
  });
}

export async function deleteAssessmentBankBranch(branchId) {
  const id = toPositiveInt(branchId);
  if (!id) throw new Error("assessment bank branch id invalid");
  return apiDelete(`/admin/assessment-bank/branches/${id}`);
}

export async function fetchAssessmentBankBranches(bankId) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("assessment bank id invalid");
  return apiGet(`/admin/assessment-bank/banks/${id}/branches`);
}

export async function createAssessmentBank(data) {
  return apiPost("/admin/assessment-bank/banks", normalizeAssessmentBankPayload(data));
}

export async function updateAssessmentBank(bankId, data) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("assessment bank id invalid");
  return apiPut(`/admin/assessment-bank/banks/${id}`, normalizeAssessmentBankPayload(data));
}

export async function deleteAssessmentBank(bankId) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("assessment bank id invalid");
  return apiDelete(`/admin/assessment-bank/banks/${id}`);
}

function normalizeAssessmentQuestionContext(input = {}) {
  const src = input && typeof input === "object" ? input : {};

  const course_id = toPositiveInt(src.course_id ?? src.courseId);
  const path_id = toPositiveInt(src.path_id ?? src.pathId);
  const day_id = toPositiveInt(src.day_id ?? src.dayId);
  const task_id = toPositiveInt(src.task_id ?? src.taskId);

  if (!course_id || !path_id || !day_id || !task_id) {
    throw new Error("assessment context required");
  }

  return { course_id, path_id, day_id, task_id };
}

function buildAssessmentTaskQuestionsPath(context, questionId = null) {
  const ids = normalizeAssessmentQuestionContext(context);
  const base =
    `/admin/assessment-bank/courses/${ids.course_id}` +
    `/paths/${ids.path_id}` +
    `/days/${ids.day_id}` +
    `/tasks/${ids.task_id}` +
    `/questions`;

  if (questionId == null) return base;

  const qid = toPositiveInt(questionId);
  if (!qid) throw new Error("question id required");

  return `${base}/${qid}`;
}

export async function fetchAssessmentBankTaskQuestions(context) {
  return apiGet(buildAssessmentTaskQuestionsPath(context));
}

export async function fetchAssessmentBankBranchQuestions(branchId) {
  const id = toPositiveInt(branchId);
  if (!id) throw new Error("assessment bank branch id invalid");
  return apiGet(`/admin/assessment-bank/branches/${id}/questions`);
}

export async function createAssessmentBankTaskQuestion(data) {
  const payload = normalizeAssessmentBankQuestionPayload(data);
  return apiPost(buildAssessmentTaskQuestionsPath(payload), payload);
}

export async function createAssessmentBankBranchQuestion(data) {
  const payload = normalizeAssessmentBankQuestionPayload(data);
  const branchId = toPositiveInt(data?.branch_id);
  if (!branchId) throw new Error("assessment bank branch id invalid");
  payload.branch_id = branchId;
  return apiPost(`/admin/assessment-bank/branches/${branchId}/questions`, payload);
}

export async function updateAssessmentBankTaskQuestion(questionId, data) {
  const payload = normalizeAssessmentBankQuestionPayload(data);
  return apiPut(buildAssessmentTaskQuestionsPath(payload, questionId), payload);
}

export async function updateAssessmentBankBranchQuestion(questionId, data) {
  const payload = normalizeAssessmentBankQuestionPayload(data);
  const branchId = toPositiveInt(data?.branch_id);
  if (!branchId) throw new Error("assessment bank branch id invalid");
  payload.branch_id = branchId;
  return apiPut(`/admin/assessment-bank/branches/${branchId}/questions/${toPositiveInt(questionId)}`, payload);
}

export async function deleteAssessmentBankTaskQuestion(questionId, context) {
  return apiDelete(buildAssessmentTaskQuestionsPath(context, questionId));
}

export async function deleteAssessmentBankBranchQuestion(questionId, branchId) {
  const qid = toPositiveInt(questionId);
  const id = toPositiveInt(branchId);
  if (!qid) throw new Error("question id required");
  if (!id) throw new Error("assessment bank branch id invalid");
  return apiDelete(`/admin/assessment-bank/branches/${id}/questions/${qid}`);
}
